import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../../app/store";

// Define the type for the active tab for the DESKTOP offcanvas
export type HeaderOffcanvasTab = "search";

// Define the possible views within the mobile offcanvas
export type MobileOffcanvasView = "main" | "category";

// Define the state shape
interface HeaderUiState {
  isHeaderActionsOffcanvasOpen: boolean;
  activeHeaderActionsOffcanvasTab: HeaderOffcanvasTab;
  isMobileOffcanvasOpen: boolean;
  mobileOffcanvasView: MobileOffcanvasView; // Track the current view in mobile offcanvas
  mobileOffcanvasParentCategoryId: number | null; // Track the parent category ID for subcategory view
  isCategoryMegaMenuOpen: boolean;
}

// Define the initial state
const initialState: HeaderUiState = {
  isHeaderActionsOffcanvasOpen: false,
  activeHeaderActionsOffcanvasTab: "search",
  isMobileOffcanvasOpen: false,
  mobileOffcanvasView: "main", // Start with the main view
  mobileOffcanvasParentCategoryId: null, // No parent selected initially
  isCategoryMegaMenuOpen: false,
};

// Create the slice
export const headerUiSlice = createSlice({
  name: "headerUi",
  initialState,
  reducers: {
    openHeaderActionsOffcanvas: (
      state,
      action: PayloadAction<HeaderOffcanvasTab>
    ) => {
      state.isHeaderActionsOffcanvasOpen = true;
      state.activeHeaderActionsOffcanvasTab = action.payload;
      state.isMobileOffcanvasOpen = false; // Close mobile offcanvas if desktop opens
      state.mobileOffcanvasView = "main"; // Reset mobile view
      state.mobileOffcanvasParentCategoryId = null;
      state.isCategoryMegaMenuOpen = false; // Close mega menu if offcanvas opens
    },
    closeHeaderActionsOffcanvas: (state) => {
      state.isHeaderActionsOffcanvasOpen = false;
    },
    setActiveHeaderActionsOffcanvasTab: (
      state,
      action: PayloadAction<HeaderOffcanvasTab>
    ) => {
      state.activeHeaderActionsOffcanvasTab = action.payload;
    },
    toggleMobileOffcanvas: (state) => {
      const willOpen = !state.isMobileOffcanvasOpen;
      state.isMobileOffcanvasOpen = willOpen;
      if (!willOpen) {
        // Reset view when closing
        state.mobileOffcanvasView = "main";
        state.mobileOffcanvasParentCategoryId = null;
      }
      state.isCategoryMegaMenuOpen = false; // Close mega menu if mobile opens
    },
    openMobileOffcanvas: (state) => {
      state.isMobileOffcanvasOpen = true;
      state.mobileOffcanvasView = "main"; // Ensure it opens to main view
      state.mobileOffcanvasParentCategoryId = null;
      state.isCategoryMegaMenuOpen = false; // Close mega menu if mobile opens
    },
    closeMobileOffcanvas: (state) => {
      state.isMobileOffcanvasOpen = false;
      // Reset view when closing
      state.mobileOffcanvasView = "main";
      state.mobileOffcanvasParentCategoryId = null;
    },
    // --- ADDED Mobile Offcanvas View Management ---
    setMobileOffcanvasView: (
      state,
      action: PayloadAction<{
        view: MobileOffcanvasView;
        parentId?: number | null;
      }>
    ) => {
      state.mobileOffcanvasView = action.payload.view;
      state.mobileOffcanvasParentCategoryId =
        action.payload.parentId !== undefined ? action.payload.parentId : null;
    },
    // --- Mega Menu Actions ---
    openCategoryMegaMenu: (state) => {
      state.isCategoryMegaMenuOpen = true;
      state.isHeaderActionsOffcanvasOpen = false; // Close other panels
      state.isMobileOffcanvasOpen = false;
      state.mobileOffcanvasView = "main"; // Reset mobile view
      state.mobileOffcanvasParentCategoryId = null;
    },
    closeCategoryMegaMenu: (state) => {
      state.isCategoryMegaMenuOpen = false;
    },
  },
});

// Export the actions
export const {
  openHeaderActionsOffcanvas,
  closeHeaderActionsOffcanvas,
  setActiveHeaderActionsOffcanvasTab,
  toggleMobileOffcanvas,
  openMobileOffcanvas,
  closeMobileOffcanvas,
  setMobileOffcanvasView,
  openCategoryMegaMenu,
  closeCategoryMegaMenu,
} = headerUiSlice.actions;

// Export selectors to easily access the state
export const selectIsHeaderActionsOffcanvasOpen = (state: RootState): boolean =>
  state.headerUi.isHeaderActionsOffcanvasOpen;
export const selectActiveHeaderActionsOffcanvasTab = (
  state: RootState
): HeaderOffcanvasTab => state.headerUi.activeHeaderActionsOffcanvasTab;
export const selectIsMobileOffcanvasOpen = (state: RootState): boolean =>
  state.headerUi.isMobileOffcanvasOpen;
export const selectMobileOffcanvasView = (
  state: RootState
): MobileOffcanvasView => state.headerUi.mobileOffcanvasView;
export const selectMobileOffcanvasParentCategoryId = (
  state: RootState
): number | null => state.headerUi.mobileOffcanvasParentCategoryId;
export const selectIsCategoryMegaMenuOpen = (state: RootState): boolean =>
  state.headerUi.isCategoryMegaMenuOpen;

// Export the reducer
export default headerUiSlice.reducer;
